'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AdBanner from '@/components/AdBanner';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import SubscriptionPanel from '@/components/SubscriptionPanel';
import { checkDiscountCode } from '@/lib/discounts';
import { fetchReviewSettings } from '@/lib/siteSettings';
import {
  DEFAULT_VAT,
  fetchPlans,
  fetchVatSettings,
  priceLabel,
  pounds,
  vatSuffix,
  type MembershipPlan,
  type VatSettings,
} from '@/lib/membership';

type BillingCycle = 'monthly' | 'annual';

// Styling only. Everything a customer reads — name, tagline, prices, features,
// button label, badge, and whether the plan is shown at all — now comes from the
// membership_plans table so the client can change strategy without a deploy.
const PLAN_STYLE: Record<string, {
  colour: string;
  headerBg: string;
  headerText: string;
  ctaClass: string;
}> = {
  free: {
    colour: 'border-border',
    headerBg: 'bg-muted',
    headerText: 'text-foreground',
    ctaClass: 'btn-outline',
  },
  pro: {
    colour: 'border-primary ring-2 ring-primary/30',
    headerBg: 'bg-primary',
    headerText: 'text-primary-foreground',
    ctaClass: 'btn-primary',
  },
  booster: {
    colour: 'border-accent ring-2 ring-accent/30',
    headerBg: 'amber-gradient',
    headerText: 'text-white',
    ctaClass: 'btn-accent',
  },
};

const FALLBACK_STYLE = PLAN_STYLE.free;

const comparisonRows = [
  { feature: 'Listings per year', ftdFree: '6', ftdPro: 'Unlimited', ftdBooster: 'Unlimited', sml: '4 (Free) / Unlimited (Gold)' },
  { feature: 'Enquiries per year', ftdFree: '20', ftdPro: 'Unlimited', ftdBooster: 'Unlimited', sml: '12 (Free) / Unlimited (Gold)' },
  { feature: 'Listing duration', ftdFree: '4 weeks', ftdPro: '10 weeks', ftdBooster: '10 weeks', sml: '4 weeks (Free) / 8 weeks (Gold)' },
  { feature: 'Listing boosts/month', ftdFree: '—', ftdPro: '2 free', ftdBooster: 'Unlimited', sml: '1 free (Gold only)' },
  { feature: 'Verified badge', ftdFree: '—', ftdPro: '✓', ftdBooster: 'Premium', sml: '✓ (Gold only)' },
  // Prices here are filled in from the live plans at render time — see priceRows
  // below — so this table can't drift from what is actually charged.
  { feature: 'Monthly price', ftdFree: '', ftdPro: '', ftdBooster: '', sml: 'Free / £14.99', live: 'monthly' },
  { feature: 'Annual price', ftdFree: '', ftdPro: '', ftdBooster: '', sml: 'Free / £149', live: 'annual' },
  { feature: 'Haulage job board', ftdFree: 'View only', ftdPro: 'Post (5/mo)', ftdBooster: 'Unlimited', sml: '—' },
  { feature: 'Timed auctions', ftdFree: '—', ftdPro: '✓', ftdBooster: '✓', sml: '—' },
  { feature: 'Hauliers directory', ftdFree: '—', ftdPro: '—', ftdBooster: '1 free (annual)', sml: '—' },
  { feature: 'Performance reports', ftdFree: '—', ftdPro: '—', ftdBooster: 'Monthly', sml: '—' },
  { feature: 'Phone support', ftdFree: '—', ftdPro: '—', ftdBooster: '✓', sml: '✓ (Gold only)' },
];

const faqs = [
  { id: 'faq-001', q: 'Can I cancel part-way through?', a: 'No — a month or a year is paid for in full and is not refundable or cancellable part-way through. What you can do at any time is switch auto-renewal off in your dashboard. You keep everything until the end of the period you have paid for, and after that your account simply returns to Free.' },
  { id: 'faq-002', q: 'Is there a free trial?', a: 'We run free trials on Pro and Booster from time to time — if one is running, the button will say so and the length will be shown before you pay. Otherwise your subscription starts straight away, and you can cancel any time before the next billing date.' },
  { id: 'faq-003', q: 'What is a listing boost?', a: 'A boost pushes your listing to the top of search results for 7 days, increasing visibility to buyers browsing that category. Pro members get 2 free boosts per month.' },
  { id: 'faq-004', q: 'How does the verification process work?', a: 'Submit your photo ID and, if you have one, your farm assurance certificate (e.g. Red Tractor). Our team verifies within 2–3 business days and adds the Verified badge to your profile.' },
  { id: 'faq-005', q: 'Are payments secure?', a: 'All payments are processed via Stripe, the UK\'s most trusted payment gateway. We never store your card details. Stripe is PCI-DSS Level 1 certified.' },
  { id: 'faq-006', q: 'Can hauliers use FarmTradeDirect?', a: 'Absolutely. Hauliers can register for free, browse and bid on haulage jobs, and purchase an annual listing in the Hauliers Directory. Booster members can also bid on jobs.' },
];

export default function MembershipPricingScreen() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);
  const { user } = useAuth();
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [vat, setVat] = useState<VatSettings>(DEFAULT_VAT);
  const [plansLoading, setPlansLoading] = useState(true);
  // 0 = no trial. Everything on this page that mentions a trial is gated on this,
  // so the page can never advertise an offer the checkout won't honour.
  const [trialDays, setTrialDays] = useState(0);
  const [showComparison, setShowComparison] = useState(true);

  // The whole card comes from the database, so the page can never advertise
  // something the checkout won't honour.
  useEffect(() => {
    Promise.all([
      fetchPlans(supabase),
      fetchVatSettings(supabase),
      fetchReviewSettings(supabase),
    ])
      .then(([planRows, vatSettings, review]) => {
        setPlans(planRows);
        setVat(vatSettings);
        setTrialDays(review.subscriptionTrialDays);
        setShowComparison(review.showComparisonTable);
      })
      .catch(() => {
        // Leave the page empty rather than showing invented prices.
      })
      .finally(() => setPlansLoading(false));
  }, [supabase]);

  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const shownPrice = (plan: MembershipPlan) =>
    billing === 'monthly'
      ? priceLabel(plan.monthly_price_pence)
      : priceLabel(plan.annual_price_pence);

  const saving = (plan: MembershipPlan) => {
    const month = pounds(plan.monthly_price_pence);
    const year = pounds(plan.annual_price_pence);
    if (month === 0 || !year) return null;
    return (month * 12 - year).toFixed(2);
  };

  // Rocket left this as a toast that pretended to redirect. It now creates a real
  // Stripe Checkout session — the plan, price and any discount are all decided
  // server-side, so the browser can't buy Booster at the Pro price.
  const handleUpgrade = async (planId: string) => {

    if (planId === 'free') {
      router.push(user ? '/post-listing' : '/sign-up-login-screen');
      return;
    }
    if (!user) {
      toast.error('Please sign in or create a free account first.');
      router.push('/sign-up-login-screen');
      return;
    }

    setSubscribing(planId);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId,
          interval: billing === 'annual' ? 'year' : 'month',
          discountCode: appliedCode || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Could not start checkout.');
      window.location.href = data.url;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start checkout.');
      setSubscribing(null);
    }
  };

  const applyCode = async () => {
    const entered = codeInput.trim();
    if (!entered) return;
    setCheckingCode(true);
    try {
      const result = await checkDiscountCode(supabase, entered);
      if (result.valid) {
        setAppliedCode(result.code ?? entered.toUpperCase());
        const off =
          result.percentOff != null
            ? `${Number(result.percentOff)}% off`
            : `£${((result.amountOffPence ?? 0) / 100).toFixed(2)} off`;
        toast.success(`Code applied — ${off} your first payment.`);
      } else {
        setAppliedCode(null);
        toast.error(result.message || 'That code is not valid.');
      }
    } finally {
      setCheckingCode(false);
    }
  };

  return (
    <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full mb-4">
          <Icon name="StarIcon" size={15} variant="solid" />
          Better value, and no commission on your sales
        </div>
        <h1 className="text-3xl xl:text-4xl font-extrabold text-foreground mb-3">
          Simple, Transparent Membership
        </h1>
        <p className="text-muted-foreground text-base max-w-xl mx-auto">
          More features, lower prices. Choose the plan that fits your farming operation.
        </p>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <span className={`text-sm font-semibold ${billing === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly</span>
          <button
            onClick={() => setBilling(billing === 'monthly' ? 'annual' : 'monthly')}
            className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${billing === 'annual' ? 'bg-primary' : 'bg-muted'}`}
            aria-label="Toggle billing cycle"
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${billing === 'annual' ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
          <span className={`text-sm font-semibold ${billing === 'annual' ? 'text-foreground' : 'text-muted-foreground'}`}>
            Annual
            <span className="ml-1.5 bg-success/15 text-success text-xs font-bold px-1.5 py-0.5 rounded-full">Save 20%</span>
          </span>
        </div>
      </div>

      {user && (
        <div className="max-w-2xl mx-auto mb-8">
          <SubscriptionPanel />
        </div>
      )}

      {vat.pricesExVat && (
        <p className="text-center text-xs text-muted-foreground mb-6">
          All prices shown exclude VAT
          {vat.chargeVat ? `. VAT at ${vat.vatRatePercent}% is added at checkout.` : '.'}
        </p>
      )}

      {/* Ad banner */}
      <div className="flex justify-center mb-8">
        <AdBanner width={728} height={90} label="728×90 Membership Page Banner" className="rounded-lg" />
      </div>

      {/* Pricing cards */}
      {/* Discount code — checked against the database before checkout, and again
          server-side when the session is created. */}
      <div className="max-w-md mx-auto mb-8">
        <label className="form-label text-center block" htmlFor="promo">
          Got a discount code?
        </label>
        <div className="flex gap-2 mt-1.5">
          <input
            id="promo"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyCode();
            }}
            placeholder="e.g. HARVEST5"
            className="form-input font-mono"
          />
          <button
            type="button"
            onClick={applyCode}
            disabled={checkingCode || !codeInput.trim()}
            className="btn-secondary whitespace-nowrap disabled:opacity-60"
          >
            {checkingCode ? 'Checking…' : 'Apply'}
          </button>
        </div>
        {appliedCode && (
          <p className="text-xs text-green-700 font-semibold mt-2 flex items-center gap-1.5">
            <Icon name="CheckCircleIcon" size={14} />
            {appliedCode} applied — it will show on your invoice at checkout.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {plansLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={`plan-sk-${i}`} className="card h-[560px] animate-pulse bg-muted/40" />
            ))
          : plans.map((plan) => {
              const style = PLAN_STYLE[plan.id] ?? FALLBACK_STYLE;
              const isPaid = plan.monthly_price_pence > 0;
              const suffix = vatSuffix(vat, billing === 'monthly' ? plan.monthly_price_pence : plan.annual_price_pence);
              return (
                <div
                  key={plan.id}
                  className={`relative flex flex-col rounded-2xl border-2 overflow-hidden shadow-card ${style.colour} ${plan.badge ? 'md:-mt-4 md:mb-4' : ''}`}
                >
                  {plan.badge && (
                    <div className="absolute top-3 right-3 bg-accent text-white text-xs font-bold px-2.5 py-1 rounded-full z-10">
                      {plan.badge}
                    </div>
                  )}

                  {/* Header */}
                  <div className={`p-6 ${style.headerBg}`}>
                    <h2 className={`text-xl font-extrabold ${style.headerText} mb-1`}>{plan.name}</h2>
                    <p className={`text-sm ${plan.id === 'free' ? 'text-muted-foreground' : 'opacity-80 text-white'} mb-4`}>
                      {plan.tagline}
                    </p>
                    <div className={`flex items-end gap-1 ${style.headerText}`}>
                      <span className="text-4xl font-extrabold tabular-nums">{shownPrice(plan)}</span>
                      {isPaid && (
                        <span className="text-sm opacity-80 mb-1.5">
                          /{billing === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      )}
                    </div>
                    {isPaid && suffix && (
                      <p className={`text-xs mt-1 ${plan.id === 'free' ? 'text-muted-foreground' : 'opacity-70 text-white'}`}>
                        {suffix}
                      </p>
                    )}
                    {billing === 'annual' && isPaid && saving(plan) && (
                      <p className={`text-xs mt-1 ${plan.id === 'free' ? 'text-success' : 'text-green-200'} font-semibold`}>
                        Save £{saving(plan)} vs monthly
                      </p>
                    )}
                    {billing === 'monthly' && isPaid && plan.annual_price_pence ? (
                      <p className={`text-xs mt-1 ${plan.id === 'free' ? 'text-muted-foreground' : 'opacity-70 text-white'}`}>
                        or {priceLabel(plan.annual_price_pence)}/yr
                      </p>
                    ) : null}
                    <p className={`text-xs mt-2 font-semibold ${plan.id === 'free' ? 'text-primary' : 'opacity-90 text-white'}`}>
                      {plan.reveals_per_year === null
                        ? 'Unlimited contact reveals'
                        : `${plan.reveals_per_year} contact reveal${plan.reveals_per_year === 1 ? '' : 's'} a year`}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="px-6 pt-4">
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={subscribing !== null}
                      className={`${style.ctaClass} w-full py-3 text-sm disabled:opacity-60`}
                    >
                      {subscribing === plan.id
                        ? 'Opening secure checkout…'
                        : trialDays > 0 && isPaid
                          ? `${plan.cta_label ?? 'Choose plan'} — ${trialDays} Days Free`
                          : plan.cta_label ?? 'Choose plan'}
                    </button>
                  </div>

                  {/* Features */}
                  <div className="p-6 flex flex-col gap-2.5 flex-1">
                    {plan.features.map((feature, fi) => (
                      <div key={`feat-${plan.id}-${fi}`} className="flex items-start gap-2.5">
                        <Icon
                          name={feature.included ? 'CheckCircleIcon' : 'XCircleIcon'}
                          size={16}
                          className={`shrink-0 mt-0.5 ${feature.included ? 'text-success' : 'text-muted-foreground/40'}`}
                          variant={feature.included ? 'solid' : 'outline'}
                        />
                        <span className={`text-sm ${feature.included ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                          {feature.label}
                          {feature.note && (
                            <span className="ml-1.5 text-xs font-bold text-accent">{feature.note}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
      </div>

      {/* Comparison table — hidden entirely when switched off in the admin. */}
      {showComparison && (
      <div className="mb-12">
        <h2 className="text-xl font-extrabold text-foreground mb-2 text-center">Competitor Comparison</h2>
        <p className="text-muted-foreground text-sm text-center mb-6">FarmTradeDirect delivers more features at lower prices — see for yourself.</p>

        <div className="overflow-x-auto rounded-2xl border border-border shadow-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 font-bold text-foreground w-1/3">Feature</th>
                <th className="text-center px-3 py-3 font-bold text-muted-foreground w-1/6">FTD Free</th>
                <th className="text-center px-3 py-3 font-bold text-primary w-1/6 bg-primary/5">FTD Pro</th>
                <th className="text-center px-3 py-3 font-bold text-accent w-1/6 bg-accent/5">FTD Booster</th>
                <th className="text-center px-3 py-3 font-bold text-muted-foreground w-1/6 bg-gray-50">Typical competitor</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, ri) => {
                // The two price rows are filled from the live plans rather than
                // typed in, so the table can't contradict the cards above it.
                const live = (row as { live?: string }).live;
                const cell = (planId: string, fallback: string) => {
                  if (!live) return fallback;
                  const plan = plans.find((pl) => pl.id === planId);
                  if (!plan) return fallback;
                  return priceLabel(
                    live === 'annual' ? plan.annual_price_pence : plan.monthly_price_pence
                  );
                };
                return (
                  <tr key={`cmp-row-${ri}`} className={`border-b border-border ${ri % 2 === 0 ? 'bg-card' : 'bg-muted/30'}`}>
                    <td className="px-4 py-3 font-medium text-foreground">{row.feature}</td>
                    <td className="px-3 py-3 text-center text-muted-foreground">{cell('free', row.ftdFree)}</td>
                    <td className="px-3 py-3 text-center font-semibold text-primary bg-primary/3">{cell('pro', row.ftdPro)}</td>
                    <td className="px-3 py-3 text-center font-semibold text-accent bg-accent/3">{cell('booster', row.ftdBooster)}</td>
                    <td className="px-3 py-3 text-center text-muted-foreground bg-gray-50/50">{row.sml}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Trust signals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { icon: 'ShieldCheckIcon', title: 'Stripe Secure Payments', desc: 'PCI-DSS Level 1 certified' },
          { icon: 'ArrowPathIcon', title: 'Cancel Anytime', desc: 'No long-term commitment' },
          { icon: 'CheckBadgeIcon', title: 'Member Verification', desc: 'Verified within 2–3 days' },
          ...(trialDays > 0
            ? [{ icon: 'StarIcon', title: `${trialDays}-Day Free Trial`, desc: 'Pro & Booster plans' }]
            : []),
        ].map((signal) => (
          <div key={`trust-${signal.title}`} className="card text-center">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Icon name={signal.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-primary" />
            </div>
            <h4 className="font-bold text-sm text-foreground mb-0.5">{signal.title}</h4>
            <p className="text-xs text-muted-foreground">{signal.desc}</p>
          </div>
        ))}
      </div>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto mb-12">
        <h2 className="text-xl font-extrabold text-foreground mb-6 text-center">Frequently Asked Questions</h2>
        <div className="flex flex-col gap-2">
          {faqs.map((faq) => (
            <div key={faq.id} className="card border border-border">
              <button
                onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <span className="font-semibold text-sm text-foreground">{faq.q}</span>
                <Icon name={openFaq === faq.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-muted-foreground shrink-0" />
              </button>
              {openFaq === faq.id && (
                <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border leading-relaxed">{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="hero-gradient rounded-2xl p-8 text-center text-primary-foreground">
        <h2 className="text-2xl font-extrabold text-white mb-2">Ready to trade smarter?</h2>
        <p className="text-green-200 mb-6 max-w-md mx-auto">List livestock, machinery and deadstock commission free. Start free today.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link href="/sign-up-login-screen" className="btn-accent px-8 py-3 text-base">
            <Icon name="UserPlusIcon" size={18} />
            Create Free Account
          </Link>
          <Link href="/marketplace-listings" className="btn-outline border-white text-white hover:bg-white/10 px-8 py-3 text-base">
            Browse Listings
          </Link>
        </div>
      </div>
    </main>
  );
}